// Little buzzes (haptics) for phones and tablets. Android vibrates through navigator.vibrate. iPhones have no
// vibrate API, but on iOS 18+ toggling a switch input plays the system's haptic tick, so a hidden one is
// clicked instead. Nothing touches the page until unlock(), so this module is safe to import in Node tests.

// Buzz/pause lengths in milliseconds, in navigator.vibrate style.
export const PATTERNS = {
  jump: [18], doubleJump: [14, 30, 14], land: [10], bounce: [22], speed: [12],
  hurt: [50, 40, 50], block: [20], fall: [40, 60, 40], hit: [25], mobDie: [15, 30, 25], stomp: [30], explosion: [60],
  coin: [8], gem: [14, 40, 14], star: [20, 40, 20, 40, 40], heart: [15], powerup: [12, 30, 12],
  checkpoint: [20, 50, 20], lucky: [15, 30, 15, 30, 30], chest: [20, 40, 40], gate: [15, 40, 15],
  correct: [12, 40, 20], combo: [12, 30, 12, 30, 12], wrong: [60], levelComplete: [30, 50, 30, 50, 80],
  boss: [80, 40, 80], bossHit: [40, 30, 40], dash: [14], place: [10], gather: [8], craft: [15, 30, 15],
  reward: [20, 40, 20], badge: [20, 40, 20, 40, 40], buy: [15], pop: [8], levelUp: [30, 40, 30],
};

// Rate limits (ms), like audio.play's: the same buzz waits 80 ms (coins 120, speed pads 500), any buzz 40 ms.
const GAP = { coin: 120, speed: 500 };
const DEFAULT_GAP = 80;
const ANY_GAP = 40;
const MAX_TICKS = 3;

export function detectHaptics(nav = globalThis.navigator, doc = globalThis.document) {
  try {
    const vibrate = typeof nav?.vibrate === 'function';
    const switches = typeof HTMLInputElement !== 'undefined' && 'switch' in HTMLInputElement.prototype;
    const touch = nav?.maxTouchPoints > 0 || !!(doc?.defaultView && 'ontouchstart' in doc.defaultView);
    return { vibrate, iosSwitch: !vibrate && switches && touch && !!doc };
  } catch (e) {
    return { vibrate: false, iosSwitch: false };
  }
}

// The iPhone switch stays rendered (display:none can stop the tick) but is invisible, untouchable and hidden
// from screen readers. Its own click/input/change events stop here, away from the game's delegated handlers.
function makeSwitch(doc) {
  const el = doc.createElement('input');
  el.type = 'checkbox';
  el.setAttribute('switch', '');
  el.setAttribute('aria-hidden', 'true');
  el.setAttribute('tabindex', '-1');
  el.style.cssText = 'position:fixed;left:0;bottom:0;width:1px;height:1px;margin:0;opacity:.01;pointer-events:none';
  for (const type of ['click', 'input', 'change']) el.addEventListener(type, (e) => e.stopPropagation());
  (doc.body || doc.documentElement).appendChild(el);
  return el;
}

export class Haptics {
  constructor({ nav = globalThis.navigator, doc = globalThis.document, now = () => performance.now(), setTimeout = (fn, ms) => globalThis.setTimeout(fn, ms) } = {}) {
    this.nav = nav;
    this.doc = doc;
    this.now = now;
    this.later = setTimeout;
    this.enabled = () => false;
    this.caps = null;
    this.unlocked = false;
    this.input = null;
    this.last = {};
    this.lastAny = -Infinity;
    this.gen = 0;
  }

  get kind() {
    return this.caps || (this.caps = detectHaptics(this.nav, this.doc));
  }

  get supported() {
    return this.kind.vibrate || this.kind.iosSwitch;
  }

  // The setting is read on every buzz, so switching it (or the player) takes effect at once.
  bind(getEnabled) {
    this.enabled = typeof getEnabled === 'function' ? getEnabled : () => !!getEnabled;
    return this;
  }

  // Call from a tap or click: phones only buzz for a page the player has touched. Safe to call again.
  unlock() {
    if (this.unlocked) return true;
    try {
      if (this.kind.iosSwitch && !this.input) this.input = makeSwitch(this.doc);
      this.unlocked = this.kind.vibrate || !!this.input;
    } catch (e) {
      /* stays locked; the next tap tries again */
    }
    return this.unlocked;
  }

  // True when a buzz started. Off, unsupported, hidden, too soon or unknown names are quietly skipped.
  play(name) {
    try {
      const pattern = Object.prototype.hasOwnProperty.call(PATTERNS, name) ? PATTERNS[name] : null;
      if (!pattern || !this.unlocked || !this.enabled() || this.doc?.hidden) return false;
      const t = this.now();
      if (t - this.lastAny < ANY_GAP || t - (this.last[name] ?? -Infinity) < (GAP[name] ?? DEFAULT_GAP)) return false;
      if (this.kind.vibrate) {
        const active = this.nav.userActivation;
        if ((active && !active.hasBeenActive) || this.nav.vibrate(pattern) === false) return false;
      } else if (this.input) this.ticks(pattern);
      else return false;
      this.last[name] = this.lastAny = t;
      return true;
    } catch (e) {
      return false;
    }
  }

  // A switch gives one fixed tick per toggle: up to three ticks, spaced by the pattern's pauses.
  ticks(pattern) {
    const gen = this.gen;
    const pauses = pattern.filter((_, i) => i % 2);
    const tick = () => {
      try {
        if (gen === this.gen && !this.doc?.hidden) this.input?.click();
      } catch (e) {
        /* ignore */
      }
    };
    tick();
    for (let k = 1, at = 0; k < Math.min(MAX_TICKS, pattern.length); k++) {
      at += pauses[Math.min(k, pauses.length) - 1];
      this.later(tick, at);
    }
  }

  // Ends a buzz in progress, including iPhone ticks still waiting.
  stop() {
    this.gen++;
    try {
      if (this.unlocked && this.kind.vibrate) this.nav.vibrate(0);
    } catch (e) {
      /* ignore */
    }
  }
}

export const haptics = new Haptics();
